/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  useContext,
  Fragment,
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react';
import {
  string,
  node,
  shape,
  func,
  arrayOf,
} from 'prop-types';
import debounce from 'lodash.debounce';
import { withRouter } from 'react-router';
import { withTracking } from 'components/tracking';

const ExperimentContext = React.createContext();

const BaseExperimentProvider = ({
  children,
  experiments,
  track,
  location,
}) => {
  const [
    isReadyToCollectPageExperiments,
    setIsReadyToCollectPageExperiments,
  ] = useState(false);
  const pageExperiments = useRef({});

  const debouncedTrackPageExperiments = useCallback(debounce(() => {
    track({
      data: {
        event: 'experiments.loaded',
        experiments: pageExperiments.current,
      },
    });
    setIsReadyToCollectPageExperiments(false);
  }, 4000), []);

  const setPageExperiment = (experiment) => {
    pageExperiments.current = {
      ...pageExperiments.current,
      ...experiment,
    };

    if (!isReadyToCollectPageExperiments) return;
    debouncedTrackPageExperiments();
  };

  useEffect(() => {
    pageExperiments.current = {};
    setIsReadyToCollectPageExperiments(true);
  }, [location.pathname]);

  return (
    <ExperimentContext.Provider
      value={{
        isReadyToCollectPageExperiments,
        experiments,
        setPageExperiment,
      }}
    >
      {children}
    </ExperimentContext.Provider>
  );
};

BaseExperimentProvider.propTypes = {
  experiments: shape({}),
  children: node.isRequired,
  track: func.isRequired,
  location: shape({}).isRequired,
};

BaseExperimentProvider.defaultProps = {
  experiments: {},
};

const ExperimentProvider = withTracking(withRouter(BaseExperimentProvider));

const Experiment = ({
  experimentId,
  testVariantId,
  children,
  controlVariantId,
  variantIds,
}) => {
  const getExperimentData = () => {
    if (process.env.EXPERIMENT_CONTEXT) {
      return JSON.parse(process.env.EXPERIMENT_CONTEXT);
    }
    return useContext(ExperimentContext);
  };

  const {
    isReadyToCollectPageExperiments,
    experiments,
    setPageExperiment,
  } = getExperimentData();
  const variantId = experiments[experimentId];

  useEffect(() => {
    if (isReadyToCollectPageExperiments && [...variantIds, controlVariantId].includes(variantId)) {
      setPageExperiment({
        [experimentId]: variantId,
      });
    }
  }, [isReadyToCollectPageExperiments]);

  return (
    <Fragment>
      {children && children(testVariantId || variantId || null)}
    </Fragment>
  );
};

Experiment.propTypes = {
  experimentId: string.isRequired,
  testVariantId: string,
  controlVariantId: string,
  variantIds: arrayOf(string).isRequired,
  children: func,
};

Experiment.defaultProps = {
  controlVariantId: '',
  testVariantId: null,
  children: null,
};

export default Experiment;
export { ExperimentProvider };
